import { inject, observer } from 'mobx-react';
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Col, Row } from 'reactstrap';

import globalTranslations from '../../../i18n/globalTranslations';
import FullProduct from '../../../models/product/FullProduct';
import { modelOf } from '../../../prop-types';
import AccountStore from '../../../store/AccountStore';
import ConfigStore from '../../../store/ConfigStore';
import CouponStore from '../../../store/CouponStore';
import AccountModalTab from '../../../types/AccountModalTab';
import RequestState from '../../../types/RequestState';
import { scrollToElementById } from '../../../util/dom';
import { consoleErrorUnlessFiltered } from '../../../util/sentryFilters';
import AccountModalLink from '../../account/AccountModalLink';
import ScrollableAnchor from '../../anchor/ScrollableAnchor';
import CouponExpiry from '../../coupon/CouponExpiry';
import CouponText from '../../coupon/CouponText';
import ContentForState from '../../loader/ContentForState';
import Review from '../../review/Review';
import ReviewAggregation from '../../review/ReviewAggregation';
import ReviewForm from '../../review/ReviewForm';

const DEFAULT_NUMBER_OF_REVIEWS_SHOWN = 6;

@observer
class ProductReviews extends Component {
  constructor(props) {
    super(props);
    this.maybeLoadReviews();

    this.state = {
      showAll: false,
    };
  }

  componentDidMount() {
    this.maybeLoadReviews();
  }

  maybeLoadReviews = () => {
    const { product } = this.props;
    if (product.reviewsState === RequestState.NONE) {
      product.loadReviews().catch((e) => {
        if (e.response && e.response.status !== 404) {
          consoleErrorUnlessFiltered(e);
        }
      });
    }
  };

  renderMoreReviewsButton = () => {
    const toggle = () => {
      this.setState({
        showAll: !this.state.showAll,
      });
    };

    if (this.props.product.reviews.length <= DEFAULT_NUMBER_OF_REVIEWS_SHOWN) {
      return null;
    }

    return (
      <Button
        color="secondary"
        onClick={toggle}
        className="ProductReviews__more-reviews-button"
      >
        {this.state.showAll ? (
          <FormattedMessage
            id="review.showDefaultTitle"
            defaultMessage="Show default number of reviews"
          />
        ) : (
          <FormattedMessage
            id="review.showAllTitle"
            defaultMessage="Show all reviews ({count})"
            values={{
              count: this.props.product.reviews.length,
            }}
          />
        )}
      </Button>
    );
  };

  renderCouponPromoBox = () => {
    const { couponStore } = this.props;
    const coupon = couponStore.reviewCoupon;

    if (!coupon) {
      return null;
    }

    return (
      <div className="ProductReviews__coupon-promo">
        <div className="ProductReviews__coupon-promo-title">
          <FormattedMessage
            id="review.reviewCouponTitle"
            defaultMessage="Review this product to receive a discount coupon!"
          />{' '}
          <span className="ProductReviews__coupon-promo-title-highlight">
            <CouponText coupon={coupon} />
          </span>
        </div>
        {coupon.minimum_order && (
          <FormattedMessage
            {...globalTranslations.couponMinimumOrderSentence}
            values={{
              minimumOrder: coupon.minimum_order + ' €',
            }}
          />
        )}{' '}
        {coupon.end_date && <CouponExpiry date={coupon.end_date} />}
      </div>
    );
  };

  render() {
    const { product, configStore, accountStore, couponStore } = this.props;
    const { reviews } = product;

    const shownReviews = this.state.showAll
      ? reviews
      : reviews.slice(0, DEFAULT_NUMBER_OF_REVIEWS_SHOWN);

    const coupon = couponStore.reviewCoupon;

    const formOrLogin =
      configStore.reviews.allowedForVisitors || accountStore.loggedIn ? (
        <ReviewForm couponsEnabled={!!coupon} product={product} />
      ) : (
        <AccountModalLink tab={AccountModalTab.LOGIN}>
          <Button tag="span" color="primary">
            <FormattedMessage {...globalTranslations.writeNewReview} />
          </Button>
        </AccountModalLink>
      );

    const isWebStore = configStore.siteConfig.isWebStore;

    return (
      <div className="ProductReviews">
        <ContentForState
          state={product.reviewsState}
          forLoaded={() => (
            <>
              {reviews.length > 0 && (
                <Row>
                  <Col md="6">
                    <ReviewAggregation reviews={reviews} />
                  </Col>
                  <Col md="6">
                    {this.renderCouponPromoBox()}
                    {isWebStore && (
                      <Button
                        color="primary"
                        onClick={() => scrollToElementById('newReviewForm')}
                      >
                        <FormattedMessage
                          {...globalTranslations.writeNewReview}
                        />
                      </Button>
                    )}
                    {this.renderMoreReviewsButton()}
                  </Col>
                </Row>
              )}
              <Row>
                {shownReviews.map((review) => (
                  <Col xs="12" md="6" key={review.id}>
                    <Review review={review} />
                  </Col>
                ))}
              </Row>
              {this.renderMoreReviewsButton()}

              {isWebStore && (
                <div className="ProductReviews__new-review">
                  <ScrollableAnchor id="newReviewForm" offset={50} />
                  <div className="ProductReviews__header">
                    <FormattedMessage
                      id="review.haveYouBoughtSentence"
                      defaultMessage="Have you purchased this product before?"
                    />
                  </div>
                  {this.renderCouponPromoBox()}
                  <div>{formOrLogin}</div>
                </div>
              )}
            </>
          )}
        />
      </div>
    );
  }
}

ProductReviews.propTypes = {
  product: modelOf(FullProduct).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  accountStore: modelOf(AccountStore).isRequired,
  couponStore: modelOf(CouponStore).isRequired,
};

export default inject(
  'configStore',
  'accountStore',
  'couponStore'
)(ProductReviews);
